import { Accordion, AccordionVariant } from '@aus-platform/design-system';
import { useAppSelector } from 'app/hooks';
import classNames from 'classnames';
import { selectMap3dDataset } from 'map-3d/shared/map-3d-slices';
import React from 'react';
import { ExpandableText } from 'shared/components';
import { Formatter, formatMeasurement } from 'shared/helpers';
import { DateTimeFormatLength } from 'shared/utils';
import { getMaterialTypeLabel } from '../../constants';
import {
  type ClusterDetailItem,
  type ClusterDetailsAccordionProps,
} from './types';

export const ClusterDetailsAccordion: React.FC<
  ClusterDetailsAccordionProps
> = ({ selectedDashboardCluster, children }) => {
  // Selectors.
  const { selectedProject, selectedTerrainSite, selectedTerrainIteration } =
    useAppSelector(selectMap3dDataset);

  // Variables.
  const {
    name,
    numberOfHeaps,
    baseReference,
    category,
    bulkDensity,
    materialType,
    remarks,
  } = selectedDashboardCluster;

  const clusterDetailItems: ClusterDetailItem[] = [
    { label: 'PROJECT', value: selectedProject?.name },
    { label: 'SITE', value: selectedTerrainSite?.name },
    {
      label: 'ITERATION',
      value: selectedTerrainIteration?.name,
    },
    {
      label: 'BASE REFERENCE',
      value: Formatter.toTitleCase(baseReference, '_'),
    },
    {
      label: 'SURVEY DATE',
      value:
        selectedTerrainIteration?.date?.formatDate(
          DateTimeFormatLength.Long,
          ' ',
        ) || '-',
    },
    {
      label: 'CATEGORY',
      value: Formatter.toTitleCase(category, '_'),
    },
    {
      label: 'BULK DENSITY',
      value: formatMeasurement(bulkDensity, 'gm/cm³'),
    },
    {
      label: 'MATERIAL',
      value: materialType ? getMaterialTypeLabel(materialType) : '-',
    },
    {
      label: 'DESCRIPTION',
      fullWidth: true,
      customRenderer: () => (
        <ExpandableText
          text={remarks?.trim() || '-'}
          className="dashboard-modal__cluster-details__description"
          maxWords={27}
        />
      ),
    },
  ];

  const heapLabel = numberOfHeaps === 1 ? 'HEAP' : 'HEAPS';

  const renderAccordionTitle = () => {
    return (
      <div className="dashboard-modal__cluster-header">
        <span className="dashboard-modal__cluster-name">{name}</span>

        <span className="dashboard-modal__cluster-divider" />

        <span className="dashboard-modal__cluster-heap-count">
          {numberOfHeaps} {heapLabel}
        </span>
      </div>
    );
  };

  return (
    <Accordion
      variant={AccordionVariant.ChevronLeft}
      defaultActiveKey={
        selectedDashboardCluster.includeInDashboard
          ? undefined
          : 'heap-cluster-details'
      }
    >
      <Accordion.Item
        eventKey="heap-cluster-details"
        title={renderAccordionTitle()}
      >
        <div className="dashboard-modal__cluster-details-grid">
          {clusterDetailItems.map(
            ({ label, value, fullWidth = false, customRenderer }) => {
              return (
                <div
                  key={label}
                  className={classNames(
                    'dashboard-modal__cluster-details-grid__item',
                    {
                      'dashboard-modal__cluster-details-grid__item--full-width':
                        fullWidth,
                    },
                  )}
                >
                  <span className="dashboard-modal__cluster-details-grid__item__label">
                    {label}
                  </span>

                  {customRenderer ? customRenderer() : <p>{value}</p>}
                </div>
              );
            },
          )}
        </div>

        {children}
      </Accordion.Item>
    </Accordion>
  );
};
